import { appDataDB } from './indexeddb'

/**
 * Main DataService class for managing all app data
 * Integrates with IndexedDB for persistent storage
 */
class DataService {
  private static instance: DataService
  private isInitialized = false
  private initializationPromise: Promise<void> | null = null

  static getInstance(): DataService {
    if (!DataService.instance) {
      DataService.instance = new DataService()
    }
    return DataService.instance
  }

  async initializeData(): Promise<void> {
    if (this.isInitialized) return
    if (this.initializationPromise) return this.initializationPromise

    this.initializationPromise = this.performInitialization()
    await this.initializationPromise
    this.initializationPromise = null
  }

  private async performInitialization(): Promise<void> {
    try {
      await appDataDB.init()

      // Check if any data exists first
      const stores = ['destinations', 'restaurants', 'hotels', 'activities', 'museums']
      const existingData = await Promise.all(
        stores.map(async store => {
          const data = await appDataDB.getData(store)
          return { store, hasData: data.length > 0 }
        })
      )
      
      const hasAnyData = existingData.some(({ hasData }) => hasData)
      
      if (!hasAnyData) {
        // No data exists, fetch from API
        console.log('No data found, fetching from API...')
        await this.fetchAndStoreAllData()
      } else {
        // Data exists, check if it's stale
        const staleChecks = await Promise.all(
          stores.map(store => appDataDB.isDataStale(store))
        )
        
        const needsRefresh = staleChecks.some(isStale => isStale)
        
        if (needsRefresh) {
          console.log('Data is stale, refreshing from API...')
          await this.fetchAndStoreAllData()
        } else {
          console.log('Data is fresh, using cached version')
        }
      }

      this.isInitialized = true
    } catch (error) {
      console.error('Failed to initialize data:', error)
      this.isInitialized = false
    }
  }

  private async fetchAndStoreAllData(): Promise<void> {
    // Fetch all data in parallel
    const [destinations, restaurants, hotels, activities, museums] =
      await Promise.all([
        this.fetchDestinations(),
        this.fetchRestaurants(),
        this.fetchHotels(),
        this.fetchActivities(),
        this.fetchMuseums(),
      ])

    // Store in IndexedDB
    await Promise.all([
      appDataDB.storeData('destinations', destinations),
      appDataDB.storeData('restaurants', restaurants),
      appDataDB.storeData('hotels', hotels),
      appDataDB.storeData('activities', activities),
      appDataDB.storeData('museums', museums),
    ])
    
    console.log('Data refreshed successfully')
  }

  private async fetchDestinations(): Promise<any[]> {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}alldestinations`
      )
      const data = await response.json()
      return data.destinations || []
    } catch (error) {
      console.error('Error fetching destinations:', error)
      return []
    }
  }

  private async fetchRestaurants(): Promise<any[]> {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}allrestaurants`
      )
      const data = await response.json()
      return data.restaurants || []
    } catch (error) {
      console.error('Error fetching restaurants:', error)
      return []
    }
  }

  private async fetchHotels(): Promise<any[]> {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}allhotels`)
      const data = await response.json()
      return data.hotels || []
    } catch (error) {
      console.error('Error fetching hotels:', error)
      return []
    }
  }

  private async fetchActivities(): Promise<any[]> {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}allactivity`)
      const data = await response.json()
      return data.activity?.data || []
    } catch (error) {
      console.error('Error fetching activities:', error)
      return []
    }
  }

  private async fetchMuseums(): Promise<any[]> {
    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}musees`)
      const data = await response.json()
      return data.musees?.data || []
    } catch (error) {
      console.error('Error fetching museums:', error)
      return []
    }
  }

  async search(query: string): Promise<any[]> {
    // Ensure data is initialized and fresh
    await this.initializeData()
    return appDataDB.searchAll(query)
  }

  async forceRefresh(): Promise<void> {
    this.isInitialized = false
    await appDataDB.clearAllData()
    await this.fetchAndStoreAllData()
    this.isInitialized = true
  }
}

// Export singleton instance
export const dataService = DataService.getInstance()

export interface GalleryImage {
  id: number
  title: string
  category: string
  image: string
}

export interface HotelGallery {
  hotelName: string
  images: GalleryImage[]
}

/**
 * Gallery service for fetching hotel images
 * Extends the existing DataService with gallery-specific functionality
 */
export const galleryService = {
  /**
   * Get a random hotel with at least 6 images
   * Fetches from IndexedDB if available, otherwise uses fallback data
   */
  async getRandomHotelGallery(): Promise<HotelGallery> {
    try {
      // Initialize the app database if needed
      await appDataDB.init()
      
      // Get all hotels from IndexedDB
      const hotels = await appDataDB.getData('hotels')
      
      if (!hotels || hotels.length === 0) {
        console.warn('No hotels found in IndexedDB, using fallback data')
        return this.getFallbackGallery()
      }

      // Filter hotels that have at least 6 images
      const hotelsWithImages = hotels.filter((hotel: any) => {
        const imageCount = hotel.images?.length || 0
        return imageCount >= 6
      })

      if (hotelsWithImages.length === 0) {
        console.warn('No hotels with 6+ images found, using fallback data')
        return this.getFallbackGallery()
      }

      // Select a random hotel
      const randomHotel = hotelsWithImages[
        Math.floor(Math.random() * hotelsWithImages.length)
      ]

      // Transform hotel data to gallery format
      return {
        hotelName: randomHotel.name || 'Hotel Gallery',
        images: this.transformImagesToGalleryFormat(randomHotel.images || []),
      }
    } catch (error) {
      console.error('Error loading hotel gallery from IndexedDB:', error)
      return this.getFallbackGallery()
    }
  },

  /**
   * Transform hotel images to gallery format
   */
  transformImagesToGalleryFormat(images: any[]): GalleryImage[] {
    return (images || [])
      .slice(0, 20) // Limit to 20 images for performance
      .map((img: any, index: number) => ({
        id: img.id || index,
        title: img.title || img.name || `Image ${index + 1}`,
        category: img.category || img.type || 'Hotel',
        image: img.image || img.url || img.src || '/placeholder.svg',
      }))
      .filter(img => img.image && img.image !== '/placeholder.svg')
  },

  /**
   * Fallback gallery data if no real hotels are available
   */
  getFallbackGallery(): HotelGallery {
    return {
      hotelName: 'Featured Hotel',
      images: [
        {
          id: 1,
          title: 'Luxury Pool',
          category: 'Resort',
          image:
            'https://images.unsplash.com/photo-1576456567190-732d57c4ffee?w=800&h=600&fit=crop',
        },
        {
          id: 2,
          title: 'Oceanfront View',
          category: 'Landscape',
          image:
            'https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=800&h=600&fit=crop',
        },
        {
          id: 3,
          title: 'Modern Bedroom',
          category: 'Rooms',
          image:
            'https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800&h=600&fit=crop',
        },
        {
          id: 4,
          title: 'Fine Dining',
          category: 'Restaurant',
          image:
            'https://images.unsplash.com/photo-1552566626-52f8b29e368c?w=800&h=600&fit=crop',
        },
        {
          id: 5,
          title: 'Spa Retreat',
          category: 'Wellness',
          image:
            'https://images.unsplash.com/photo-1604534319590-ad52efb62371?w=800&h=600&fit=crop',
        },
        {
          id: 6,
          title: 'Beach Lounge',
          category: 'Interior',
          image:
            'https://images.unsplash.com/photo-1564078369132-fdb3c3d40ced?w=800&h=600&fit=crop',
        },
      ],
    }
  },

  /**
   * Get all available hotels with image count
   */
  async getAllHotels(): Promise<
    Array<{ id: number; name: string; imageCount: number }>
  > {
    try {
      await appDataDB.init()
      const hotels = await appDataDB.getData('hotels')

      return (hotels || [])
        .map((hotel: any) => ({
          id: hotel.id,
          name: hotel.name || 'Unknown Hotel',
          imageCount: hotel.images?.length || 0,
        }))
        .filter((h: any) => h.imageCount >= 6)
    } catch (error) {
      console.error('Error fetching hotels:', error)
      return []
    }
  },
}
